import React, { useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { SCOPES } from '@/features/config/scopes';
import { AdminDataGrid } from '../../shared/AdminDataGrid';
import { useAdminListParams } from '../../shared/useAdminListParams';
import { useListFormFieldConfigsQuery } from '../api/formFieldConfigsApi';
import {
	COPY_FORM_FIELD_CONFIGS_DIALOG,
	CopyFormFieldConfigsDialog,
} from '../CopyFormFieldConfigsDialog';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';
import { CONFIG_TYPE, isScopeChosen } from '../scopes';
import { formFieldConfigColumns } from './columns';
import { ResetFormFieldConfigsButton } from './ResetFormFieldConfigsButton';

// Text settings edited in their cells
const TEXT_FIELDS = ['label', 'helperText', 'description'];

/**
 * The form field configs the page's scope sees, one per field, showing the form's unsaved
 * values. Text cells and switches change the Formik values; the footer saves them. Ticked rows
 * can be copied (CopyFormFieldConfigsDialog) or reset (ResetFormFieldConfigsButton) once there
 * are no unsaved changes. Rows are ticked by field name, so a tick stays on the field when a
 * save gives it a new config id, and Copy and Reset send the current ids.
 */
export const FormFieldConfigsTable = () => {
	const dispatch = useDispatch();
	const { scope } = useFormFieldConfigScope();
	const { values, dirty, setFieldValue } = useFormikContext();
	const { gridProps } = useAdminListParams('formFieldConfigs');
	// currentData, not data: data keeps the previous scope's rows while a new scope loads
	const {
		currentData: configs,
		isFetching,
		isError,
	} = useListFormFieldConfigsQuery(
		{ ...scope, configType: CONFIG_TYPE },
		// Empty until a ledger or broker group is picked
		{ skip: !isScopeChosen(scope) },
	);
	const [selectedNames, setSelectedNames] = useState([]);
	const isCopyOpen = useSelector((state) => state.dialog.open[COPY_FORM_FIELD_CONFIGS_DIALOG]);

	const rows = useMemo(
		() => (configs ?? []).map((config) => ({ ...config, ...values[config.id] })),
		[configs, values],
	);
	const selectedConfigs = rows.filter((row) => selectedNames.includes(row.name));

	// Keeps edited text in the form
	const handleRowUpdate = (newRow, oldRow) => {
		TEXT_FIELDS.filter((field) => newRow[field] !== oldRow[field]).forEach((field) =>
			setFieldValue(`${oldRow.id}.${field}`, newRow[field]),
		);
		return newRow;
	};

	return (
		<>
			<AdminDataGrid
				table='formFieldConfigs'
				columns={
					// The default is never a copy
					scope.scope === SCOPES.DEFAULT
						? formFieldConfigColumns.filter((column) => column.field !== 'copiedFrom')
						: formFieldConfigColumns
				}
				rows={rows}
				getRowId={(row) => row.name}
				isLoading={isFetching}
				isError={isError}
				objectType='form field configs'
				minHeight='75vh'
				processRowUpdate={handleRowUpdate}
				checkboxSelection
				disableRowSelectionOnClick
				rowSelectionModel={selectedNames}
				onRowSelectionModelChange={setSelectedNames}
				toolbarActions={
					<Stack direction='row' spacing={1} sx={{ ml: 'auto' }}>
						<ResetFormFieldConfigsButton configs={selectedConfigs} disabled={dirty} />
						<Button
							variant='table_actions'
							disabled={!selectedConfigs.length || dirty}
							onClick={() =>
								dispatch(
									openDialog({
										dialogName: COPY_FORM_FIELD_CONFIGS_DIALOG,
										data: { docIds: selectedConfigs.map((config) => config.id) },
									}),
								)
							}
						>
							{selectedConfigs.length ? `Copy (${selectedConfigs.length})` : 'Copy'}
						</Button>
					</Stack>
				}
				{...gridProps}
			/>
			{/* Outside the grid, so typing in its pickers isn't caught by the grid's keyboard handling */}
			{isCopyOpen && <CopyFormFieldConfigsDialog />}
		</>
	);
};

FormFieldConfigsTable.displayName = 'FormFieldConfigsTable';
